import React, { useState, useEffect } from 'react';
import { formatCurrency, formatPercentage, formatChangedAmount, getChangeColorClass, parseFloatFromString } from '../utils/formatters';

const STORAGE_KEY = 'portfolioMetrics_showAltcoins';

const PortfolioMetrics = ({
  portfolio,
  holdingsValue,
  currentPnl,
  currentRoi,
  valueChange24h,
  valueChangePercentage24h,
  // Altcoin stats (excl. BTC)
  altcoinHoldings,
  altcoinPnl,
  altcoinRoi,
  altcoinChange24h,
  altcoinChangePercentage24h,
  // Altcoin stats (excl. BTC+ETH)
  altcoinExEthHoldings,
  altcoinExEthPnl,
  altcoinExEthRoi,
  altcoinExEthChange24h,
  altcoinExEthChangePercentage24h,
  onOpenPortfolioManager,
  // Override the portfolio's crypto counts (e.g. stocks mode counts its own rows)
  transactionsCount,
  positionsCount,
  changeLabel = '24h Change',
  accountingUnavailable = false,
}) => {
  const [showAltcoins, setShowAltcoins] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Persist state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, showAltcoins.toString());
    } catch {
      // Ignore storage errors
    }
  }, [showAltcoins]);

  const txCount = transactionsCount ?? portfolio?.transactionsCount ?? 0;
  const posCount = positionsCount ?? portfolio?.positionsCount ?? 0;
  const txLabel = txCount === 1 ? 'tx' : 'txs';
  const posLabel = posCount === 1 ? 'position' : 'positions';

  // Parse altcoin values
  const hv = parseFloatFromString(holdingsValue || '0');
  const altHoldings = parseFloatFromString(altcoinHoldings || '0');
  const altPnl = parseFloatFromString(altcoinPnl || '0');
  const altRoi = parseFloatFromString(altcoinRoi || '0');
  const alt24h = parseFloatFromString(altcoinChange24h || '0');
  const alt24hPct = parseFloatFromString(altcoinChangePercentage24h || '0');

  const altExEthHoldings = parseFloatFromString(altcoinExEthHoldings || '0');
  const altExEthPnl = parseFloatFromString(altcoinExEthPnl || '0');
  const altExEthRoi = parseFloatFromString(altcoinExEthRoi || '0');
  const altExEth24h = parseFloatFromString(altcoinExEthChange24h || '0');
  const altExEth24hPct = parseFloatFromString(altcoinExEthChangePercentage24h || '0');

  // Show altcoins section if there are any altcoins (holdings different from total)
  const hasAltcoins = altHoldings > 0 && altHoldings < hv;
  const hasAltcoinsExEth = altExEthHoldings > 0 && altExEthHoldings < altHoldings;

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 space-y-4 border border-gray-200 dark:border-gray-700">
      {/* Portfolio Header */}
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            {portfolio?.name || '—'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {txCount} {txLabel} · {posCount} {posLabel}
          </p>
        </div>
        {onOpenPortfolioManager && (
          <button
            onClick={onOpenPortfolioManager}
            className="px-3 py-1.5 text-sm rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
          >
            Change
          </button>
        )}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Holdings */}
        <div>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">Holdings</p>
          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(holdingsValue)}
          </p>
        </div>

        {/* Current PnL */}
        <div>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">Current PnL</p>
          <div className="flex items-baseline gap-2">
            <p className={`text-xl font-bold ${getChangeColorClass(currentPnl)}`}>
              {accountingUnavailable ? '—' : formatChangedAmount(currentPnl)}
            </p>
            <p className={`text-sm ${getChangeColorClass(currentRoi)}`}>
              {accountingUnavailable ? '' : `(${formatPercentage(currentRoi)})`}
            </p>
          </div>
        </div>

        {/* 24h Change */}
        <div>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">{changeLabel}</p>
          <div className="flex items-baseline gap-2">
            <p className={`text-xl font-bold ${getChangeColorClass(valueChange24h)}`}>
              {formatChangedAmount(valueChange24h)}
            </p>
            <p className={`text-sm ${getChangeColorClass(valueChangePercentage24h)}`}>
              ({formatPercentage(valueChangePercentage24h)})
            </p>
          </div>
        </div>
      </div>

      {/* Altcoin Stats Toggle */}
      {hasAltcoins && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <button
            onClick={() => setShowAltcoins(!showAltcoins)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            {showAltcoins ? 'Hide' : 'Show'} Altcoin Stats
          </button>

          {showAltcoins && (
            <div className="mt-3 space-y-2">
              {/* Altcoins (excl. BTC) */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                {altHoldings > 0 && (
                  <div>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400">Altcoin Holdings</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(altHoldings)}
                    </p>
                  </div>
                )}
                {altPnl !== 0 && (
                  <div>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400">Current PnL</p>
                    <div className="flex items-baseline gap-1">
                      <p className={`font-semibold ${getChangeColorClass(altPnl)}`}>
                        {formatChangedAmount(altPnl)}
                      </p>
                      <p className={`text-xs ${getChangeColorClass(altRoi)}`}>
                        ({formatPercentage(altRoi)})
                      </p>
                    </div>
                  </div>
                )}
                {alt24h !== 0 && (
                  <div>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400">24h Change</p>
                    <div className="flex items-baseline gap-1">
                      <p className={`font-semibold ${getChangeColorClass(alt24h)}`}>
                        {formatChangedAmount(alt24h)}
                      </p>
                      <p className={`text-xs ${getChangeColorClass(alt24hPct)}`}>
                        ({formatPercentage(alt24hPct)})
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Altcoins (excl. BTC+ETH) */}
              {hasAltcoinsExEth && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  {altExEthHoldings > 0 && (
                    <div>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">Alts excl. ETH Holdings</p>
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        {formatCurrency(altExEthHoldings)}
                      </p>
                    </div>
                  )}
                  {altExEthPnl !== 0 && (
                    <div>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">Current PnL</p>
                      <div className="flex items-baseline gap-1">
                        <p className={`font-semibold ${getChangeColorClass(altExEthPnl)}`}>
                          {formatChangedAmount(altExEthPnl)}
                        </p>
                        <p className={`text-xs ${getChangeColorClass(altExEthRoi)}`}>
                          ({formatPercentage(altExEthRoi)})
                        </p>
                      </div>
                    </div>
                  )}
                  {altExEth24h !== 0 && (
                    <div>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">24h Change</p>
                      <div className="flex items-baseline gap-1">
                        <p className={`font-semibold ${getChangeColorClass(altExEth24h)}`}>
                          {formatChangedAmount(altExEth24h)}
                        </p>
                        <p className={`text-xs ${getChangeColorClass(altExEth24hPct)}`}>
                          ({formatPercentage(altExEth24hPct)})
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortfolioMetrics;
